// Mock data for AI Audience Explorer V2 demo mode
// Uses v2 criteriaGroups 2D array schema

// Delay constants (in milliseconds)
export const DELAYS = {
	GENERATION: 5000, // 5 seconds for 4-step pipeline
	SIZING: 800, // 0.8 seconds for sizing calculations
};

// Mock generation results (v2 schema with criteriaGroups)
// Each criteria type is its own group; within a group, criteria are split by sub-type.
// siteContent uses targetType: "contextual" (realtime) or "behavioral" (content consumption).
export const MOCK_RESULTS = {
	requirements_summary:
		"Build targeted audiences for tech-savvy professionals interested in sustainable technology and green energy solutions, focusing on decision-makers in urban areas.",
	audiences: [
		{
			name: "Green Tech Decision Makers",
			differentiation:
				"Focuses on business decision-makers and influencers interested in sustainable technology adoption and green energy solutions.",
			composite_conditions: {
				criteriaGroupCondition: "AND",
				criteriaGroups: [
					// Group 1: Realtime Site Content
					[
						{
							siteContent: {
								targetType: "contextual",
								action: "include",
								criteria: {
									type: "categories",
									categories: [
										{ text: "Technology & Computing", id: 596 },
										{ text: "Business", id: 53 },
										{ text: "Environment", id: 467 },
										{ text: "Power and Energy Industry", id: 110 },
									],
								},
							},
						},
						{
							siteContent: {
								targetType: "contextual",
								action: "include",
								criteria: {
									type: "keywords",
									keywords: [
										"sustainable technology",
										"green energy",
										"renewable power",
										"clean tech",
										"sustainability",
										"carbon neutral",
										"eco-friendly tech",
									],
								},
							},
						},
					],
					// Group 2: Demographics
					[
						{
							demographics: {
								age: [
									{ id: 54, title: "25-29" },
									{ id: 56, title: "30-34" },
									{ id: 58, title: "35-39" },
									{ id: 59, title: "40-44" },
									{ id: 61, title: "45-49" },
									{ id: 62, title: "50-54" },
								],
								gender: [],
								householdIncome: [
									{ id: 57, title: "£70,000 to £99,999" },
									{ id: 58, title: "£100,000 to £149,999" },
									{ id: 59, title: "£150,000 or more" },
								],
							},
						},
					],
					// Group 3: Syndicated
					[
						{
							syndicated: {
								segments: [
									{
										id: "1001",
										title: "Business & Finance > Decision Makers > C-Suite Executives - £1.75 CPM",
										provider: "Acxiom",
										category: "Business & Finance",
										unique_users: 0,
									},
									{
										id: "1002",
										title: "Technology > Innovation > Early Adopters & Tech Enthusiasts - £1.40 CPM",
										provider: "Acxiom",
										category: "Technology",
										unique_users: 0,
									},
									{
										id: "2001",
										title: "Lifestyle > Green Living > Sustainability Advocates - £1.15 CPM",
										provider: "Experian",
										category: "Lifestyle > Green Living",
										unique_users: 0,
									},
								],
							},
						},
					],
					// Group 4: Location
					[
						{
							location: {
								countries: [],
								regions: [],
								metros: [],
								cities: [
									{ id: "GB-ENG|London", city_name: "London", region_code: "GB-ENG", region_name: "England", country_code: "GB", country_name: "United Kingdom" },
									{ id: "GB-ENG|Manchester", city_name: "Manchester", region_code: "GB-ENG", region_name: "England", country_code: "GB", country_name: "United Kingdom" },
									{ id: "GB-ENG|Birmingham", city_name: "Birmingham", region_code: "GB-ENG", region_name: "England", country_code: "GB", country_name: "United Kingdom" },
									{ id: "GB-ENG|Bristol", city_name: "Bristol", region_code: "GB-ENG", region_name: "England", country_code: "GB", country_name: "United Kingdom" },
									{ id: "GB-ENG|Leeds", city_name: "Leeds", region_code: "GB-ENG", region_name: "England", country_code: "GB", country_name: "United Kingdom" },
									{ id: "GB-SCT|Glasgow", city_name: "Glasgow", region_code: "GB-SCT", region_name: "Scotland", country_code: "GB", country_name: "United Kingdom" },
								],
								postalCodes: [],
								action: "include",
							},
						},
					],
				],
			},
		},
		{
			name: "Urban Sustainability Enthusiasts",
			theme: "Urban + Lifestyle + Environment",
			differentiation:
				"Targets environmentally conscious urban professionals who actively seek sustainable lifestyle choices and eco-friendly products.",
			composite_conditions: {
				criteriaGroupCondition: "AND",
				criteriaGroups: [
					// Group 1: Realtime Site Content
					[
						{
							siteContent: {
								targetType: "contextual",
								action: "include",
								criteria: {
									type: "categories",
									categories: [
										{ text: "Style & Fashion", id: 552 },
										{ text: "Environment", id: 467 },
										{ text: "Home & Garden", id: 274 },
										{ text: "Green Solutions", id: 78 },
									],
								},
							},
						},
						{
							siteContent: {
								targetType: "contextual",
								action: "include",
								criteria: {
									type: "keywords",
									keywords: [
										"eco-friendly",
										"sustainable living",
										"green products",
										"zero waste",
										"organic",
										"environmentally conscious",
										"urban sustainability",
									],
								},
							},
						},
					],
					// Group 2: Demographics
					[
						{
							demographics: {
								age: [
									{ id: 54, title: "25-29" },
									{ id: 56, title: "30-34" },
									{ id: 58, title: "35-39" },
									{ id: 59, title: "40-44" },
								],
								gender: [],
								householdIncome: [
									{ id: 55, title: "£50,000 to £59,999" },
									{ id: 56, title: "£60,000 to £69,999" },
									{ id: 57, title: "£70,000 to £99,999" },
									{ id: 58, title: "£100,000 to £149,999" },
								],
							},
						},
					],
					// Group 3: Location
					[
						{
							location: {
								countries: [],
								regions: [],
								metros: [],
								cities: [
									{ id: "GB-ENG|London", city_name: "London", region_code: "GB-ENG", region_name: "England", country_code: "GB", country_name: "United Kingdom" },
									{ id: "GB-ENG|Sheffield", city_name: "Sheffield", region_code: "GB-ENG", region_name: "England", country_code: "GB", country_name: "United Kingdom" },
									{ id: "GB-ENG|Bristol", city_name: "Bristol", region_code: "GB-ENG", region_name: "England", country_code: "GB", country_name: "United Kingdom" },
									{ id: "GB-ENG|Manchester", city_name: "Manchester", region_code: "GB-ENG", region_name: "England", country_code: "GB", country_name: "United Kingdom" },
									{ id: "GB-SCT|Glasgow", city_name: "Glasgow", region_code: "GB-SCT", region_name: "Scotland", country_code: "GB", country_name: "United Kingdom" },
									{ id: "GB-ENG|Nottingham", city_name: "Nottingham", region_code: "GB-ENG", region_name: "England", country_code: "GB", country_name: "United Kingdom" },
								],
								postalCodes: [],
								action: "include",
							},
						},
					],
				],
			},
		},
		{
			name: "Clean Energy Advocates",
			theme: "Energy + Policy + Innovation",
			differentiation:
				"Reaches policy-oriented professionals and activists focused on clean energy policy, innovation, and environmental advocacy.",
			composite_conditions: {
				criteriaGroupCondition: "AND",
				criteriaGroups: [
					// Group 1: Realtime Site Content
					[
						{
							siteContent: {
								targetType: "contextual",
								action: "include",
								criteria: {
									type: "categories",
									categories: [
										{ text: "News and Politics", id: 379 },
										{ text: "Power and Energy Industry", id: 110 },
										{ text: "Science", id: 464 },
										{ text: "Technology & Computing", id: 596 },
									],
								},
							},
						},
						{
							siteContent: {
								targetType: "contextual",
								action: "include",
								criteria: {
									type: "keywords",
									keywords: [
										"clean energy",
										"renewable energy policy",
										"climate action",
										"solar power",
										"wind energy",
										"energy innovation",
										"environmental policy",
									],
								},
							},
						},
					],
					// Group 2: Technographics
					[
						{
							technographics: {
								os: {},
								browser: {},
								deviceType: {
									desktop: true,
									mobile: false,
									tablet: true,
									tv: false,
								},
							},
						},
					],
					// Group 3: Location
					[
						{
							location: {
								countries: [],
								regions: [],
								metros: [],
								cities: [
									{ id: "GB-ENG|London", city_name: "London", region_code: "GB-ENG", region_name: "England", country_code: "GB", country_name: "United Kingdom" },
									{ id: "GB-ENG|Leeds", city_name: "Leeds", region_code: "GB-ENG", region_name: "England", country_code: "GB", country_name: "United Kingdom" },
									{ id: "GB-ENG|Newcastle", city_name: "Newcastle", region_code: "GB-ENG", region_name: "England", country_code: "GB", country_name: "United Kingdom" },
									{ id: "GB-WLS|Cardiff", city_name: "Cardiff", region_code: "GB-WLS", region_name: "Wales", country_code: "GB", country_name: "United Kingdom" },
									{ id: "GB-SCT|Glasgow", city_name: "Glasgow", region_code: "GB-SCT", region_name: "Scotland", country_code: "GB", country_name: "United Kingdom" },
								],
								postalCodes: [],
								action: "include",
							},
						},
					],
				],
			},
		},
	],
};

// Mock debug info (v2 4-step pipeline)
export const MOCK_DEBUG_INFO = {
	timing: {
		step1_extract: 1243,
		step2_plan: 987,
		step3_build: 1654,
		step4_assemble: 1102,
		post_processing: 412,
		total: 5398,
	},
	request_info: {
		model: "gpt-4.1",
		provider: "openai",
		temperature: 0.7,
		targeting_mode: "full",
		silo: { name: "Demo Publisher", id: 999 },
	},
	step1_extraction_response: {
		requirements_summary:
			"Build targeted audiences for tech-savvy professionals interested in sustainable technology and green energy solutions, focusing on decision-makers in urban areas.",
		strategy_analysis:
			"The request targets professionals with high engagement in sustainability and technology. Focus on urban markets with strong tech and environmental sectors.",
		audience_profile:
			"Educated professionals (bachelor's degree or higher), ages 25-64, with household incomes above £50K. Primarily located in major tech hubs and urban centres.",
		content_context:
			"Content interests span technology adoption, sustainable business practices, renewable energy developments, eco-friendly lifestyle choices, and environmental policy.",
	},
	step2_audience_plan: {
		audiences: [
			{
				name: "Green Tech Decision Makers",
				theme: "Business + Technology + Sustainability",
				strategy: "Target C-suite and senior decision-makers reading technology and sustainability content",
			},
			{
				name: "Urban Sustainability Enthusiasts",
				theme: "Urban + Lifestyle + Environment",
				strategy: "Target eco-conscious urban professionals interested in sustainable lifestyle products",
			},
			{
				name: "Clean Energy Advocates",
				theme: "Energy + Policy + Innovation",
				strategy: "Target policy-oriented professionals and activists focused on clean energy and innovation",
			},
		],
	},
	step3_criteria_data: [
		{
			audience_index: 0,
			criteria_types: ["siteContent", "demographics", "syndicated", "location"],
			matched_categories: 4,
			matched_keywords: 7,
			matched_segments: 3,
			matched_locations: 6,
		},
		{
			audience_index: 1,
			criteria_types: ["siteContent", "demographics", "location"],
			matched_categories: 4,
			matched_keywords: 7,
			matched_locations: 6,
		},
		{
			audience_index: 2,
			criteria_types: ["siteContent", "technographics", "location"],
			matched_categories: 4,
			matched_keywords: 7,
			matched_locations: 5,
		},
	],
	step4_assembly_responses: [
		{
			audience_index: 0,
			groups_count: 4,
			criteria_group_condition: "AND",
			validation_warnings: [],
		},
		{
			audience_index: 1,
			groups_count: 3,
			criteria_group_condition: "AND",
			validation_warnings: ["Some syndicated segments may have limited reach in selected markets"],
		},
		{
			audience_index: 2,
			groups_count: 3,
			criteria_group_condition: "AND",
			validation_warnings: [],
		},
	],
};

// Mock audience sizing data
export const MOCK_AUDIENCE_SIZING = {
	0: {
		uniqueUsers: 2847392,
		pageViews: 18934821,
	},
	1: {
		uniqueUsers: 4192847,
		pageViews: 24781293,
	},
	2: {
		uniqueUsers: 1938471,
		pageViews: 12847362,
	},
};

// ============================================================================
// TARGETING MODE VARIANTS
// ============================================================================

// Helper to strip 3rd party criteria (demographics, syndicated) from criteriaGroups
function stripThirdPartyFromGroups(groups) {
	return groups
		.map((group) =>
			group.filter((criterion) => {
				const key = Object.keys(criterion)[0];
				return key !== "demographics" && key !== "syndicated";
			})
		)
		.filter((group) => group.length > 0);
}

// Helper to strip to contextual only (keep siteContent, technographics, location, domains)
function stripToContextualOnly(groups) {
	const contextualKeys = ["siteContent", "technographics", "location", "domains"];
	return groups
		.map((group) =>
			group.filter((criterion) => {
				const key = Object.keys(criterion)[0];
				return contextualKeys.includes(key);
			})
		)
		.filter((group) => group.length > 0);
}

export function getMockResultsForMode(targetingMode) {
	if (targetingMode === "full") return MOCK_RESULTS;

	const stripFn = targetingMode === "contextual_only" ? stripToContextualOnly : stripThirdPartyFromGroups;

	return {
		...MOCK_RESULTS,
		audiences: MOCK_RESULTS.audiences.map((aud) => ({
			...aud,
			composite_conditions: {
				...aud.composite_conditions,
				criteriaGroups: stripFn(aud.composite_conditions.criteriaGroups),
			},
		})),
	};
}

export function getMockDebugInfoForMode(targetingMode) {
	if (targetingMode === "full") return MOCK_DEBUG_INFO;

	return {
		...MOCK_DEBUG_INFO,
		request_info: {
			...MOCK_DEBUG_INFO.request_info,
			targeting_mode: targetingMode,
		},
	};
}
